import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { CommonContext } from "hooks/CommonContext";
import { FaArrowRight, FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash, FaChevronUp } from "react-icons/fa";
import { MdVideocam, MdMic, MdScreenShare, MdCheck } from "react-icons/md";
import PropTypes from "prop-types";
import CameraJoinPageBox from "./CameraJoinPageBox";
import AllowMicroCameraPermissionBox from "components/ui-components/AllowMicroCameraPermissionBox";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  .cjp-page {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    background: #080d18;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    background-image:
      radial-gradient(ellipse 55% 50% at 15% 60%, rgba(14,165,233,0.05) 0%, transparent 65%),
      radial-gradient(ellipse 45% 45% at 85% 35%, rgba(99,102,241,0.06) 0%, transparent 65%);
  }

  .cjp-body {
    flex: 1 1 auto;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 24px 24px;
    scrollbar-width: thin;
    scrollbar-color: #1a2540 transparent;
  }

  .cjp-title {
    font-size: clamp(22px, 3vw, 30px);
    font-weight: 700;
    color: #f0f4ff;
    letter-spacing: -0.025em;
    margin: 0 0 12px;
    text-align: center;
  }

  .cjp-subtitle {
    font-size: 15px;
    color: #8b9dc3;
    line-height: 1.65;
    max-width: 620px;
    text-align: center;
    margin: 0 auto 28px;
  }

  .cjp-chips {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 32px;
  }

  .cjp-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(34, 211, 238, 0.07);
    border: 1px solid rgba(34, 211, 238, 0.18);
    border-radius: 50px;
  }

  .cjp-chip-icon { color: #22d3ee; font-size: 18px; display: flex; align-items: center; flex-shrink: 0; }
  .cjp-chip-label { font-size: 13px; font-weight: 500; color: #c4d0ea; white-space: nowrap; }

  .cjp-cam-col {
    width: clamp(360px, 50vw, 540px);
    max-width: 100%;
    filter: drop-shadow(0 8px 40px rgba(0,0,0,0.55));
  }

  .cjp-cam-inner {
    border-radius: 18px;
    overflow: hidden;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    width: 100%;
  }

  /* ── Device selectors row ── */
  .cjp-devices {
    margin-top: 10px;
    display: flex;
    gap: 8px;
    width: 100%;
  }

  .cjp-dev-group {
    position: relative;
    display: flex;
    align-items: center;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 10px;
    flex: 1;
    min-width: 0;
    transition: border-color 0.15s;
  }

  .cjp-dev-group:focus-within,
  .cjp-dev-group:hover { border-color: #263554; }

  .cjp-dev-toggle {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 10px;
    cursor: pointer;
    flex: 1;
    min-width: 0;
    font-size: 12.5px;
    font-weight: 500;
    color: #c4d0ea;
    transition: color 0.14s;
  }

  .cjp-dev-toggle:hover { color: #f0f4ff; }
  .cjp-dev-toggle.cjp-dev-off { color: #fca5a5; }

  .cjp-dev-toggle-icon { font-size: 14px; flex-shrink: 0; }

  .cjp-dev-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .cjp-dev-sep { width: 1px; height: 20px; background: #1e2d4a; flex-shrink: 0; }

  .cjp-dev-chevron {
    all: unset;
    box-sizing: border-box;
    width: 32px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #4a5f82;
    font-size: 10px;
    flex-shrink: 0;
    border-radius: 0 9px 9px 0;
    transition: color 0.14s, background 0.14s, transform 0.2s;
  }

  .cjp-dev-chevron:hover { color: #c4d0ea; background: rgba(255,255,255,0.04); }
  .cjp-dev-chevron.cjp-dev-open { color: #22d3ee; transform: rotate(180deg); }

  .cjp-dev-dropdown {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    right: 0;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 12px;
    padding: 6px;
    z-index: 300;
    box-shadow: 0 16px 48px rgba(0,0,0,0.6);
    min-width: 220px;
    animation: cjpDdIn 0.12s ease;
  }

  @keyframes cjpDdIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .cjp-dev-dd-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #4a5f82;
    padding: 6px 10px 4px;
  }

  .cjp-dev-option {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    color: #8b9dc3;
    transition: background 0.12s, color 0.12s;
  }

  .cjp-dev-option:hover { background: rgba(34,211,238,0.07); color: #e8eeff; }
  .cjp-dev-option.cjp-dev-sel { color: #22d3ee; font-weight: 600; }

  .cjp-dev-check { font-size: 15px; color: #22d3ee; flex-shrink: 0; visibility: hidden; }
  .cjp-dev-sel .cjp-dev-check { visibility: visible; }

  .cjp-dev-opt-name {
    flex: 1; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .cjp-dev-none { padding: 8px 10px; font-size: 12.5px; color: #4a5f82; }

  /* ── Mic level indicator ── */
  .cjp-mic-bar {
    margin-top: 10px;
    width: 100%;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 10px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  .cjp-mic-bar.cjp-mic-active { border-color: rgba(74, 222, 128, 0.35); }

  .cjp-mic-bar-icon {
    font-size: 15px;
    color: #4a5f82;
    flex-shrink: 0;
    transition: color 0.2s;
  }

  .cjp-mic-bar.cjp-mic-active .cjp-mic-bar-icon { color: #4ade80; }

  .cjp-mic-bar-label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7fa8;
    flex-shrink: 0;
    width: 36px;
  }

  .cjp-mic-bar.cjp-mic-active .cjp-mic-bar-label { color: #4ade80; }

  /* The track */
  .cjp-mic-track {
    flex: 1;
    height: 4px;
    background: #1e2d4a;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .cjp-mic-fill {
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, #22d3ee 0%, #4ade80 100%);
    transition: width 0.08s ease-out;
    min-width: 0;
  }

  /* Animated bars (equalizer) shown when speaking */
  .cjp-mic-eq {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 18px;
    flex-shrink: 0;
  }

  .cjp-mic-eq-bar {
    width: 3px;
    border-radius: 2px;
    background: #4ade80;
    transition: height 0.08s ease-out;
    min-height: 2px;
  }

  /* ── Footer ── */
  .cjp-footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 32px;
    background: #0b1120;
    border-top: 1px solid #1a2540;
  }

  .cjp-help { font-size: 13.5px; color: #6b7fa8; }
  .cjp-help a { color: #22d3ee; text-decoration: none; font-weight: 600; }
  .cjp-help a:hover { text-decoration: underline; }

  .cjp-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 28px;
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
    color: #04111a;
    border: none;
    border-radius: 50px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(34, 211, 238, 0.28);
    transition: transform 0.15s ease, box-shadow 0.18s ease, filter 0.18s ease;
    white-space: nowrap;
    letter-spacing: 0.01em;
  }

  .cjp-btn:hover:not(:disabled) {
    filter: brightness(1.08); transform: translateY(-1px);
    box-shadow: 0 6px 28px rgba(34, 211, 238, 0.40);
  }

  .cjp-btn:active:not(:disabled) { transform: translateY(0); }

  .cjp-btn:disabled {
    background: #1e2d4a; color: #526080;
    cursor: not-allowed; box-shadow: none; filter: none;
  }

  @media (max-width: 600px) {
    /* Body scrolls freely, but footer is always pinned above browser chrome */
    .cjp-page {
      position: relative;
    }
    .cjp-body {
      padding: 20px 12px 12px;
      /* Leave space for the pinned footer */
      padding-bottom: 80px;
    }
    .cjp-footer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 12px 16px;
      background: #0b1120;
      border-top: 1px solid #1a2540;
      z-index: 50;
    }
    .cjp-help    { display: none; }
    .cjp-btn     { width: 100%; justify-content: center; }
    .cjp-title   { font-size: 20px; }
    .cjp-subtitle { font-size: 13px; margin-bottom: 16px; }
    .cjp-chips   { gap: 8px; margin-bottom: 16px; }
    .cjp-cam-col { width: 100% !important; min-width: 0; }
    .cjp-devices { flex-direction: column; gap: 6px; width: 100%; }
    .cjp-mic-bar { width: 100%; margin-top: 6px; }
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

/* ── useDeviceSelector ────────────────────────────────────────────────────── */
const useDeviceSelector = () => {
  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState("");
  const [micId, setMicId] = useState("");

  const enumerate = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() => { });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === "videoinput");
      const auds = devices.filter((d) => d.kind === "audioinput");
      setCameras(vids);
      setMics(auds);
      setCamId((prev) => prev || vids[0]?.deviceId || "");
      setMicId((prev) => prev || auds[0]?.deviceId || "");
    } catch (e) {
      console.warn("[DeviceSelector] enumerate failed", e);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    enumerate();
    navigator.mediaDevices.addEventListener("devicechange", enumerate);
    return () => navigator.mediaDevices.removeEventListener("devicechange", enumerate);
  }, [enumerate]);

  const switchCamera = useCallback(async (deviceId) => {
    setCamId(deviceId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } });
      const [track] = stream.getVideoTracks();
      window.dispatchEvent(new CustomEvent("bizro:switchCamera", { detail: { deviceId, track } }));
    } catch (e) { console.warn("[DeviceSelector] switchCamera failed", e); }
  }, []);

  const switchMic = useCallback(async (deviceId) => {
    setMicId(deviceId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } } });
      const [track] = stream.getAudioTracks();
      window.dispatchEvent(new CustomEvent("bizro:switchMic", { detail: { deviceId, track } }));
    } catch (e) { console.warn("[DeviceSelector] switchMic failed", e); }
  }, []);

  return { cameras, mics, camId, micId, switchCamera, switchMic };
};

/* ── useMicLevel ──────────────────────────────────────────────────────────────
   Uses Web Audio API to measure real-time mic volume (0–100).
   Returns { level, isSpeaking } so we can animate the bar and equalizer.
──────────────────────────────────────────────────────────────────────────── */
const useMicLevel = (micId, audioEnabled) => {
  const [level, setLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!audioEnabled) {
      setLevel(0);
      setIsSpeaking(false);
      return;
    }

    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: micId ? { deviceId: { exact: micId } } : true,
          video: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;

        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);
        ctxRef.current = ctx;
        analyserRef.current = analyser;

        const buf = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (cancelled) return;
          analyser.getByteFrequencyData(buf);
          const avg = buf.reduce((s, v) => s + v, 0) / buf.length;
          const pct = Math.min(100, Math.round((avg / 128) * 100));
          setLevel(pct);
          setIsSpeaking(pct > 8);
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        console.warn("[MicLevel] failed", e);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ctxRef.current) ctxRef.current.close().catch(() => { });
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [micId, audioEnabled]);

  return { level, isSpeaking };
};

/* ── DeviceGroup ─────────────────────────────────────────────────────────── */
const DeviceGroup = ({ icon, offIcon, isOn, onToggle, devices, selectedId, onSelect, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = devices.find((d) => d.deviceId === selectedId);
  const displayName = selected?.label || label;

  return (
    <div className="cjp-dev-group" ref={ref}>
      <button
        className={`cjp-dev-toggle${!isOn ? " cjp-dev-off" : ""}`}
        onClick={onToggle}
        title={isOn ? `Turn off ${label}` : `Turn on ${label}`}
      >
        <span className="cjp-dev-toggle-icon">{isOn ? icon : offIcon}</span>
        <span className="cjp-dev-label">{displayName}</span>
      </button>
      <div className="cjp-dev-sep" />
      <button
        className={`cjp-dev-chevron${open ? " cjp-dev-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={`Choose ${label}`}
      >
        <FaChevronUp />
      </button>

      {open && (
        <div className="cjp-dev-dropdown">
          <div className="cjp-dev-dd-label">Select {label}</div>
          {devices.length === 0
            ? <div className="cjp-dev-none">No devices found</div>
            : devices.map((d, i) => (
              <button
                key={d.deviceId}
                className={`cjp-dev-option${d.deviceId === selectedId ? " cjp-dev-sel" : ""}`}
                onClick={() => { onSelect(d.deviceId); setOpen(false); }}
              >
                <MdCheck className="cjp-dev-check" />
                <span className="cjp-dev-opt-name">{d.label || `${label} ${i + 1}`}</span>
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
};

DeviceGroup.propTypes = {
  icon: PropTypes.node, offIcon: PropTypes.node,
  isOn: PropTypes.bool, onToggle: PropTypes.func,
  devices: PropTypes.array, selectedId: PropTypes.string,
  onSelect: PropTypes.func, label: PropTypes.string,
};

/* ── MicLevelBar ─────────────────────────────────────────────────────────────
   Shows a real-time level track + a 4-bar animated equalizer when speaking.
──────────────────────────────────────────────────────────────────────────── */
const EQ_BARS = 4;
const MicLevelBar = ({ level, isSpeaking, audioEnabled }) => {
  // Generate per-bar heights from level with slight variance
  const barHeights = Array.from({ length: EQ_BARS }, (_, i) => {
    if (!isSpeaking) return 2;
    const variance = [1, 1.4, 0.8, 1.2][i];
    return Math.max(3, Math.min(18, Math.round(level * 0.18 * variance)));
  });

  return (
    <div className={`cjp-mic-bar${isSpeaking ? " cjp-mic-active" : ""}`}>
      {/* Icon */}
      <span className="cjp-mic-bar-icon">
        {audioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
      </span>

      {/* Label */}
      <span className="cjp-mic-bar-label">
        {!audioEnabled ? "Muted" : isSpeaking ? "Live" : "Mic"}
      </span>

      {/* Level track */}
      <div className="cjp-mic-track">
        <div
          className="cjp-mic-fill"
          style={{ width: audioEnabled ? `${level}%` : "0%" }}
        />
      </div>

      {/* Equalizer bars */}
      <div className="cjp-mic-eq">
        {barHeights.map((h, i) => (
          <div
            key={i}
            className="cjp-mic-eq-bar"
            style={{ height: `${h}px`, opacity: isSpeaking ? 1 : 0.25 }}
          />
        ))}
      </div>
    </div>
  );
};

MicLevelBar.propTypes = {
  level: PropTypes.number, isSpeaking: PropTypes.bool, audioEnabled: PropTypes.bool,
};

/* ── CandidateJoinPage ──────────────────────────────────────────────────── */
const CandidateJoinPage = ({ joinRoomHandler }) => {
  inject();
  const { setUserName, userOwnData, microphoneError, toggleVideo, toggleAudio, audioEnabled } =
    useContext(CommonContext);

  useEffect(() => {
    if (userOwnData.fname.length > 0) {
      setUserName(userOwnData.fname + " " + userOwnData.lname);
    }
    // eslint-disable-next-line
  }, []);

  // Detect low bandwidth so we can warn the candidate before they join
  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  const isLowBandwidth =
    conn?.effectiveType === "slow-2g" ||
    conn?.effectiveType === "2g" ||
    (conn?.downlink !== undefined && conn.downlink < 1);

  const { cameras, mics, camId, micId, switchCamera, switchMic } = useDeviceSelector();
  const { level, isSpeaking } = useMicLevel(micId, audioEnabled && !microphoneError);

  return (
    <div className="cjp-page">
      <div className="cjp-body">
        <h2 className="cjp-title">Check Your Camera and Mic</h2>

        <p className="cjp-subtitle">
          We use audio, video and screen sharing to generate an accurate
          assessment &amp; proctoring score. Please note that this recording will
          be available to your recruiter.
        </p>

        {isLowBandwidth && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 16px",
              marginBottom: "16px",
              background: "rgba(251, 191, 36, 0.10)",
              border: "1px solid rgba(251, 191, 36, 0.35)",
              borderRadius: "10px",
              fontSize: "13px",
              color: "#fbbf24",
              maxWidth: "540px",
              width: "100%",
            }}
          >
            <span style={{ fontSize: "16px", flexShrink: 0 }}>⚠</span>
            <span>
              Your connection appears slow. Video quality may be reduced during
              the interview.
            </span>
          </div>
        )}

        {/* Feature chips */}
        <div className="cjp-chips">
          {[
            { icon: <MdVideocam />, label: "Video" },
            { icon: <MdMic />, label: "Audio" },
            { icon: <MdScreenShare />, label: "Screen Share" },
          ].map(({ icon, label }) => (
            <div className="cjp-chip" key={label}>
              <span className="cjp-chip-icon">{icon}</span>
              <span className="cjp-chip-label">{label}</span>
            </div>
          ))}
        </div>

        {/* Camera preview */}
        <div className="cjp-cam-col">
          <div className="cjp-cam-inner">
            {microphoneError
              ? <AllowMicroCameraPermissionBox />
              : <CameraJoinPageBox />}
          </div>

          {/* Device selectors */}
          {!microphoneError && (
            <>
              <div className="cjp-devices">
                <DeviceGroup
                  icon={<FaVideo />}
                  offIcon={<FaVideoSlash />}
                  isOn={!!userOwnData?.isCameraOn}
                  onToggle={toggleVideo}
                  devices={cameras}
                  selectedId={camId}
                  onSelect={switchCamera}
                  label="Camera"
                />
                <DeviceGroup
                  icon={<FaMicrophone />}
                  offIcon={<FaMicrophoneSlash />}
                  isOn={!!audioEnabled}
                  onToggle={toggleAudio}
                  devices={mics}
                  selectedId={micId}
                  onSelect={switchMic}
                  label="Microphone"
                />
              </div>

              {/* Mic level indicator */}
              <MicLevelBar
                level={level}
                isSpeaking={isSpeaking}
                audioEnabled={!!audioEnabled}
              />
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="cjp-footer">
        <div className="cjp-help">
          Need help?{" "}
          <a href="mailto:info@niyantras.com">info@niyantras.com</a>
        </div>
        <button
          className="cjp-btn"
          onClick={() => joinRoomHandler()}
          disabled={!!microphoneError}
        >
          Join Interview
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

CandidateJoinPage.propTypes = { joinRoomHandler: PropTypes.any };
export default CandidateJoinPage;